import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuMic, LuMicOff } from "react-icons/lu";
import "./VoiceInputButton.css";

// Browser dictation (Web Speech API). Nothing is sent anywhere by this component: the recognised text is
// handed to `onText`, which appends it to the field; the user still sends or saves the form himself.
const LANGUAGES = { fr: "fr-FR", en: "en-US", ar: "ar-TN", "ar-TN": "ar-TN" };
export const MAX_LISTENING_MS = 60000;

export const speechRecognition = () =>
  (typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition)) || null;

export const recognitionLanguage = (language) => LANGUAGES[language] || LANGUAGES[String(language || "").split("-")[0]] || "fr-FR";

// Appends dictated text after the existing text (one space between), within the field's max length.
export function appendDictation(current, addition, maxLength = Infinity) {
  const before = String(current || ""), added = String(addition || "").trim();
  if (!added) return before;
  const separator = before && !/\s$/.test(before) ? " " : "";
  return (before + separator + added).slice(0, maxLength);
}

const DENIED = new Set(["not-allowed", "service-not-allowed"]);

export default function VoiceInputButton({ onText, disabled = false, className = "" }) {
  const { t, i18n } = useTranslation();
  const [listening, setListening] = useState(false);
  const [message, setMessage] = useState("");
  const recognition = useRef(null);
  const timer = useRef(null);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;
  const Recognition = speechRecognition();

  const finish = () => {
    clearTimeout(timer.current);
    timer.current = null;
    recognition.current = null;
    setListening(false);
  };

  useEffect(() => () => {
    clearTimeout(timer.current);
    const current = recognition.current;
    recognition.current = null;
    if (current) { current.onresult = current.onerror = current.onend = null; current.abort(); }
  }, []);

  if (!Recognition) return null;

  const start = () => {
    setMessage("");
    const Engine = speechRecognition() || Recognition;
    const instance = new Engine();
    instance.lang = recognitionLanguage(i18n.resolvedLanguage || i18n.language);
    instance.continuous = false; // the browser stops after a silence
    instance.interimResults = false;
    instance.maxAlternatives = 1;
    instance.onresult = (event) => {
      const text = Array.from(event.results).slice(event.resultIndex)
        .filter((result) => result.isFinal !== false).map((result) => result[0]?.transcript || "").join(" ").trim();
      if (text) onTextRef.current?.(text);
    };
    instance.onerror = (event) => {
      if (event.error === "aborted") return;
      setMessage(t(DENIED.has(event.error) ? "voice.denied" : event.error === "no-speech" ? "voice.noSpeech" : "voice.error"));
    };
    instance.onend = () => { if (recognition.current === instance) finish(); };
    recognition.current = instance;
    try {
      instance.start();
    } catch {
      finish();
      setMessage(t("voice.error"));
      return;
    }
    setListening(true);
    timer.current = setTimeout(() => instance.stop(), MAX_LISTENING_MS);
  };

  const toggle = () => {
    if (recognition.current) recognition.current.stop();
    else start();
  };

  const label = t(listening ? "voice.stop" : "voice.start");
  return (
    <span className={`lina-voice ${className}`}>
      <button type="button" className={`lina-voice-button${listening ? " is-listening" : ""}`} onClick={toggle}
        disabled={disabled && !listening} aria-pressed={listening} aria-label={label} title={label}>
        {listening ? <LuMicOff aria-hidden="true" /> : <LuMic aria-hidden="true" />}
      </button>
      {(listening || message) && (
        <span className="lina-voice-message" role={message ? "alert" : "status"}>{message || t("voice.listening")}</span>
      )}
    </span>
  );
}
